// Settings.js - Updated import function with proper thread and message handling
import React, { useState, useEffect, useRef } from 'react';
import './Settings.css';

// Fixed import path - pointing to parent directory
import { encryptData, decryptData } from '../encryptionUtils';
// Import the thread service for IndexedDB operations
import threadService from '../services/threadService';
// Import FreeTrialManager with correct path
// Try different potential paths:
import FreeTrialManager from './FreeTrialManager'; // Same directory as Settings.js

// Fallback mechanism if import fails
const tryGetFreeTrialManager = () => {
  // This function helps handle cases where FreeTrialManager might be at a different path
  try {
    // Return the imported module if successful
    return FreeTrialManager;
  } catch (err) {
    console.warn("Could not load FreeTrialManager, trial state tracking disabled:", err);
    // Return a dummy object with no-op methods to prevent errors
    return {
      enterSettingsDialog: () => console.log("FreeTrialManager not available: enterSettingsDialog"),
      exitSettingsDialog: () => console.log("FreeTrialManager not available: exitSettingsDialog"),
      ensureExpiredStateConsistency: () => console.log("FreeTrialManager not available: ensureExpiredStateConsistency"),
      resetTrialStateFlags: () => console.log("FreeTrialManager not available: resetTrialStateFlags")
    };
  }
};

// Get a reference to the FreeTrialManager (real or fallback)
const trialManager = tryGetFreeTrialManager();

const CLAUDE_MODELS = [
  { id: "claude-3-7-sonnet-20250219", name: "Claude 3.7 Sonnet" },
  { id: "claude-3-opus-20240229", name: "Claude 3 Opus" },
  { id: "claude-3-sonnet-20240229", name: "Claude 3 Sonnet" },
  { id: "claude-3-5-haiku-20241022", name: "Claude 3.5 Haiku" },
  { id: "claude-2.1", name: "Claude 2.1" }
];

const CONTEXT_LEVELS = [
  { id: "maximum", name: "Maximum (100%)", description: "Include as much conversation history as possible", value: 1.0 },
  { id: "standard", name: "Standard (50%)", description: "Include about half of the maximum allowed conversation history", value: 0.5 },
  { id: "minimum", name: "Minimum (20%)", description: "Include minimal conversation history for faster responses", value: 0.2 }
];

function Settings({ isOpen, onClose, onSave, children }) {
  const [apiKey, setApiKey] = useState('');
  const [maskedKey, setMaskedKey] = useState('');
  const [selectedModel, setSelectedModel] = useState('claude-3-7-sonnet-20250219');
  const [contextLevel, setContextLevel] = useState('maximum');
  const [showApiKey, setShowApiKey] = useState(false);
  const [hasStoredKey, setHasStoredKey] = useState(false);
  const [showResourceWarnings, setShowResourceWarnings] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [importProgress, setImportProgress] = useState(0);
  const [showImportOptions, setShowImportOptions] = useState(false);
  const [importFile, setImportFile] = useState(null);
  
  const fileInputRef = useRef(null);

  // CRITICAL FIX: Track settings dialog state in FreeTrialManager
  useEffect(() => {
    if (isOpen) {
      // Notify FreeTrialManager that we've entered settings
      console.log("Settings: Notifying FreeTrialManager that settings dialog opened");
      if (trialManager && typeof trialManager.enterSettingsDialog === 'function') {
        trialManager.enterSettingsDialog();
      }
    }
    
    // When dialog closes, notify FreeTrialManager
    return () => {
      if (!isOpen) return; // Only run on actual unmount when dialog was open
      
      console.log("Settings: Notifying FreeTrialManager that settings dialog closed");
      if (trialManager && typeof trialManager.exitSettingsDialog === 'function') {
        trialManager.exitSettingsDialog();
      }
      
      // Ensure expired state consistency
      if (trialManager && typeof trialManager.ensureExpiredStateConsistency === 'function') {
        console.log("Settings: Ensuring trial state consistency after dialog close");
        trialManager.ensureExpiredStateConsistency();
      }
    };
  }, [isOpen]);

  // Define fallback encryption functions in case import fails
  const fallbackEncrypt = (text) => {
    if (!text) return '';
    return text; // Just returns plaintext if real encryption not available
  };
  
  const fallbackDecrypt = (text) => {
    if (!text) return '';
    return text; // Just returns the text if real decryption not available
  };
  
  // Use real functions if available, otherwise use fallbacks
  const encrypt = typeof encryptData === 'function' ? encryptData : fallbackEncrypt;
  const decrypt = typeof decryptData === 'function' ? decryptData : fallbackDecrypt;

  // Load settings when component opens
  useEffect(() => {
    if (!isOpen) return;
    
    // Load settings from localStorage when component mounts or is opened
    const encryptedApiKey = localStorage.getItem('anthropic_api_key_encrypted') || '';
    const plainApiKey = localStorage.getItem('anthropic_api_key') || ''; // For backward compatibility
    const savedModel = localStorage.getItem('anthropic_model') || 'claude-3-7-sonnet-20250219';
    const savedContextLevel = localStorage.getItem('conversation_context_level') || 'maximum';
    
    console.log("Saved context level from localStorage:", savedContextLevel);
    const savedShowResourceWarnings = localStorage.getItem('show_resource_warnings');
    const showWarnings = savedShowResourceWarnings !== null ? savedShowResourceWarnings === 'true' : true; // Default to true
    
    setShowResourceWarnings(showWarnings);
    
    let keyToUse = '';
    let hasKey = false;
    
    // Try encrypted key first
    if (encryptedApiKey) {
      try {
        const decryptedApiKey = decrypt(encryptedApiKey);
        if (decryptedApiKey && decryptedApiKey.trim() !== '') {
          keyToUse = decryptedApiKey;
          hasKey = true;
          
          // Create masked version (keep first 4 chars, mask the rest)
          const prefix = decryptedApiKey.substring(0, 4);
          const masked = prefix + '*'.repeat(Math.max(8, decryptedApiKey.length - 4));
          setMaskedKey(masked);
        }
      } catch (err) {
        console.error("Error decrypting API key:", err);
      }
    }
    // If no encrypted key or decryption failed, try plain key as fallback
    if (!hasKey && plainApiKey) {
      keyToUse = plainApiKey;
      hasKey = true;
      
      // Create masked version for plain key too
      const prefix = plainApiKey.substring(0, 4);
      const masked = prefix + '*'.repeat(Math.max(8, plainApiKey.length - 4));
      setMaskedKey(masked);
      
      // Upgrade to encrypted storage if we had a plain key
      try {
        const encryptedKey = encrypt(plainApiKey);
        localStorage.setItem('anthropic_api_key_encrypted', encryptedKey);
      } catch (err) {
        console.error("Error encrypting legacy API key:", err);
      }
    }
    
    setApiKey(keyToUse);
    setHasStoredKey(hasKey);
    setSelectedModel(savedModel);
    setContextLevel(savedContextLevel);
    
    // Always start with API key hidden
    setShowApiKey(false);
    
    // Reset import dialog state when component opens
    setShowImportOptions(false);
    setImportFile(null);
  }, [isOpen, decrypt, encrypt]);

  const handleApiKeyChange = (e) => {
    const newValue = e.target.value;
    setApiKey(newValue);
    
    // If user is editing, don't use masking
    if (!showApiKey) {
      setShowApiKey(true);
    }
  };

  // CRITICAL FIX: Modified handleSave to properly handle exit
  const handleSave = () => {
    console.log("Saving settings with context level:", contextLevel);
    
    // Save API key using both methods for compatibility
    if (apiKey) {
      try {
        // New encrypted method
        const encryptedApiKey = encrypt(apiKey);
        localStorage.setItem('anthropic_api_key_encrypted', encryptedApiKey);
        
        // Legacy plain method (can be removed later if all users are migrated)
        localStorage.setItem('anthropic_api_key', apiKey);
        
        setHasStoredKey(true);
      } catch (err) {
        console.error("Error saving API key:", err);
        // Fall back to plaintext if encryption fails
        localStorage.setItem('anthropic_api_key', apiKey);
      }
    }
    
    // Save other settings
    localStorage.setItem('anthropic_model', selectedModel);
    localStorage.setItem('conversation_context_level', contextLevel);
    localStorage.setItem('show_resource_warnings', showResourceWarnings.toString());
    
    // Notify parent component with all settings
    onSave({
      apiKey: apiKey,
      model: selectedModel,
      contextLevel: contextLevel,
      showResourceWarnings: showResourceWarnings
    });
    
    // CRITICAL FIX: Properly handle exit by calling onClose()
    // This ensures our cleanup effect runs
    onClose();
  };

  // CRITICAL FIX: Modified onClose to ensure proper cleanup
  const handleClose = () => {
    console.log("Settings: Closing dialog through handleClose");
    // Call the provided onClose function
    onClose();
  };

  const toggleApiKeyVisibility = () => {
    setShowApiKey(!showApiKey);
  };

  // Export conversations to a JSON file with prompt library support
  const handleExportData = async () => {
    try {
      setIsExporting(true);
      setExportProgress(10);

      // Get prompt library from localStorage with better error handling
      let promptLibrary = [];
      try {
        // Check both possible localStorage keys for prompts
        const promptLibraryJson = localStorage.getItem("prompt_library") || localStorage.getItem("promptLibrary");
        if (promptLibraryJson) {
          promptLibrary = JSON.parse(promptLibraryJson);
          console.log(`Exporting ${promptLibrary.length} prompts from library`);
        } else {
          console.log("No prompt library found in localStorage");
        }
        
        // Also check for user_prompts key (alternative storage location)
        if (promptLibrary.length === 0) {
          const userPromptsJson = localStorage.getItem("user_prompts");
          if (userPromptsJson) {
            try {
              const userPrompts = JSON.parse(userPromptsJson);
              if (Array.isArray(userPrompts)) {
                promptLibrary = userPrompts;
                console.log(`Found ${promptLibrary.length} prompts in user_prompts key`);
              }
            } catch (e) {
              console.error("Error parsing user_prompts:", e);
            }
          }
        }
      } catch (err) {
        console.error("Error parsing prompt library:", err);
        promptLibrary = []; // Use empty array as fallback
      }
      
      // Standardize prompts format
      promptLibrary = promptLibrary.map(prompt => {
        return {
          id: prompt.id || Math.random().toString(36).substring(2, 9),
          // Include both name and title to ensure compatibility
          name: prompt.name || prompt.title || "Untitled Prompt",
          title: prompt.title || prompt.name || "Untitled Prompt",
          content: prompt.content || prompt.text || prompt.prompt || "",
          tags: Array.isArray(prompt.tags) ? [...prompt.tags] : ["MyPrompts"],
          createdAt: prompt.createdAt || Date.now(),
          lastUsedAt: prompt.lastUsedAt || null,
          useCount: prompt.useCount || 0,
          source: prompt.source || "user"
        };
      });
      
      // Get thread IDs first
      setExportProgress(20);
      const threadList = await threadService.getAllThreads();
      
      // Then get the COMPLETE threads with messages (this is what was missing)
      setExportProgress(30);
      const fullThreads = [];
      let counter = 0;
      const total = threadList.length;
      
      for (const thread of threadList) {
        // Get complete thread including all messages
        const completeThread = await threadService.getThread(thread.id);
        fullThreads.push(completeThread);
        
        // Update progress
        counter++;
        setExportProgress(30 + Math.floor((counter / total) * 40));
      }
      
      // Create export data object with all data
      const exportData = {
        version: "1.1", // Updated version for IndexedDB format
        timestamp: Date.now(),
        appName: "Blue Comet",
        data: {
          topics: fullThreads, // Now using COMPLETE threads with messages
          promptLibrary: promptLibrary // Use the parsed array, not the raw JSON string
        }
      };
      
      // Log what we're exporting for debugging
      console.log(`Exporting ${fullThreads.length} threads and ${promptLibrary.length} prompts`);
      
      // Convert to JSON string
      setExportProgress(80);
      const jsonData = JSON.stringify(exportData, null, 2);
      
      // Create the file name with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `blue-comet-backup-${date}.json`;
      
      // Create a File object
      const file = new Blob([jsonData], { type: "application/json" });
      setExportProgress(90);

      // Use the showSaveFilePicker API if available (modern browsers)
      if (window.showSaveFilePicker) {
        const saveFile = async () => {
          try {
            const opts = {
              suggestedName: filename,
              types: [{
                description: 'JSON File',
                accept: {'application/json': ['.json']},
              }],
            };
            
            const fileHandle = await window.showSaveFilePicker(opts);
            const writable = await fileHandle.createWritable();
            await writable.write(file);
            await writable.close();
            
            const successMessage = `Conversations exported successfully! Included ${fullThreads.length} conversations and ${promptLibrary.length} prompts.`;
            alert(successMessage);
          } catch (err) {
            // User cancelled or API error
            if (err.name !== 'AbortError') {
              console.error("Error saving file:", err);
              fallbackExport();
            }
          } finally {
            setIsExporting(false);
            setExportProgress(0);
          }
        };
        
        saveFile();
      } else {
        // Fallback for browsers that don't support the File System Access API
        fallbackExport();
      }
      
      // Fallback export function using traditional download
      function fallbackExport() {
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        
        // Clean up
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          setIsExporting(false);
          setExportProgress(0);
        }, 0);
        
        const successMessage = `Conversations exported successfully! Included ${fullThreads.length} conversations and ${promptLibrary.length} prompts.`;
        alert(successMessage);
      }
    } catch (error) {
      console.error("Export error:", error);
      alert("Failed to export data. Error: " + error.message);
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  // Show import options dialog when a file is selected
  const showImportDialog = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    setShowImportOptions(true);
  };
  
  // Close import options dialog
  const closeImportDialog = () => {
    setShowImportOptions(false);
    setImportFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Process the import with the selected option (combine or replace)
  const processImport = async (shouldCombine) => {
    if (!importFile) return;
    
    setIsImporting(true);
    setImportProgress(10);
    setShowImportOptions(false);
    
    // Declare this at the top level so it's accessible throughout
    let totalPromptsAfterImport = 0;
    
    const reader = new FileReader();
    
    reader.onload = async (event) => {
      try {
        // Parse the imported JSON
        const importedData = JSON.parse(event.target.result);
        setImportProgress(30);
        
        // Support both old and new format
        const isNewFormat = importedData.data && typeof importedData.data === 'object';
        
        // Get threads/topics from appropriate place in the structure
        const importedTopics = isNewFormat ? importedData.data.topics : importedData.data;
        
        // Validate imported data
        if (!Array.isArray(importedTopics)) {
          throw new Error("Invalid import file format: topics data is not an array");
        }
        
        // Process prompt library first
        let promptsImported = 0;
        try {
          if (isNewFormat && Array.isArray(importedData.data.promptLibrary)) {
            console.log(`Found ${importedData.data.promptLibrary.length} prompts in import file`);
            
            // Map prompts to the expected format with both name and title fields
            const processedPrompts = importedData.data.promptLibrary.map(prompt => {
              return {
                id: prompt.id || Math.random().toString(36).substring(2, 9),
                title: prompt.title || prompt.name || "Untitled Prompt",
                name: prompt.name || prompt.title || "Untitled Prompt",
                content: prompt.content || prompt.text || prompt.prompt || "",
                tags: Array.isArray(prompt.tags) ? [...prompt.tags] : ["MyPrompts"],
                createdAt: prompt.createdAt || Date.now(),
                lastUsedAt: prompt.lastUsedAt || null,
                useCount: prompt.useCount || 0,
                source: prompt.source || "user"
              };
            });
            
            // For prompt library, handle based on combine/replace choice
            if (shouldCombine) {
              // Get existing prompts from any of the possible storage keys
              let existingPrompts = [];
              try {
                const promptKeys = ["prompt_library", "promptLibrary", "user_prompts"];
                for (const key of promptKeys) {
                  const existingPromptsJson = localStorage.getItem(key);
                  if (existingPromptsJson) {
                    try {
                      const parsed = JSON.parse(existingPromptsJson);
                      if (Array.isArray(parsed) && parsed.length > 0) {
                        existingPrompts = parsed;
                        console.log(`Found ${existingPrompts.length} existing prompts in ${key}`);
                        break;
                      }
                    } catch (e) {
                      console.error(`Error parsing prompts from ${key}:`, e);
                    }
                  }
                }
              } catch (err) {
                console.error("Error getting existing prompts:", err);
                existingPrompts = [];
              }
              
              // Filter out duplicates by ID
              const existingIds = new Set(existingPrompts.map(p => p.id));
              const newPrompts = processedPrompts.filter(p => !existingIds.has(p.id));
              
              console.log(`Adding ${newPrompts.length} new prompts to ${existingPrompts.length} existing prompts`);
              promptsImported = newPrompts.length;
              
              // Merge prompts
              const mergedPrompts = [...existingPrompts, ...newPrompts];
              totalPromptsAfterImport = mergedPrompts.length;
              
              // Save to all localStorage keys for compatibility
              localStorage.setItem("prompt_library", JSON.stringify(mergedPrompts));
              localStorage.setItem("promptLibrary", JSON.stringify(mergedPrompts));
              localStorage.setItem("user_prompts", JSON.stringify(mergedPrompts));
              
              console.log(`Saved ${mergedPrompts.length} total prompts`);
            } else {
              // Replace all prompts
              console.log(`Replacing all prompts with ${processedPrompts.length} prompts from import`);
              promptsImported = processedPrompts.length;
              totalPromptsAfterImport = processedPrompts.length;
              
              localStorage.setItem("prompt_library", JSON.stringify(processedPrompts));
              localStorage.setItem("promptLibrary", JSON.stringify(processedPrompts));
              localStorage.setItem("user_prompts", JSON.stringify(processedPrompts));
            }
            
            // Dispatch events to notify the app that prompts changed
            window.dispatchEvent(new CustomEvent('promptLibraryChanged'));
            window.dispatchEvent(new CustomEvent('userPromptsUpdated'));
          } else {
            console.log("No prompt library found in import file");
          }
        } catch (promptErr) {
          console.error("Error processing prompt library:", promptErr);
        }
        
        // Process threads
        if (shouldCombine) {
          // Combine with existing data
          setImportProgress(40);
          
          // Get existing threads
          const existingThreads = await threadService.getAllThreads();
          const existingIds = new Set(existingThreads.map(t => t.id));
          
          // Filter out duplicates
          const newTopics = importedTopics.filter(t => !existingIds.has(t.id));
          setImportProgress(60);
          
          // Import each new thread WITH MESSAGES
          let importedCount = 0;
          let importErrors = 0;
          
          for (const topic of newTopics) {
            try {
              // Validate topic
              if (!topic.id || !topic.title) {
                console.error("Invalid topic:", topic);
                importErrors++;
                continue;
              }
              
              // Ensure messages array exists
              if (!Array.isArray(topic.messages)) {
                topic.messages = [];
              }
              
              // Create the thread first
              const threadId = await threadService.createThread(
                topic.title, 
                topic.model || 'claude-3-7-sonnet-20250219',
                topic.id, // Pass the original ID
                topic.createdAt,
                topic.lastUpdated
              );
              
              console.log(`Created thread ${threadId} with title: ${topic.title}`);
              
              // Now import each message
              for (const message of topic.messages) {
                try {
                  if (message.role === 'user') {
                    await threadService.addUserMessage(threadId, {
                      role: 'user',
                      content: message.content,
                      apiContent: message.apiContent || message.content,
                      files: message.files || []
                    }, message.id, message.timestamp);
                  } else if (message.role === 'assistant') {
                    await threadService.addAssistantMessage(
                      threadId,
                      message.content,
                      {
                        continuationInProgress: message.continuationInProgress || false,
                        continuationComplete: message.continuationComplete || true
                      },
                      message.id,
                      message.timestamp
                    );
                  }
                } catch (msgErr) {
                  console.error(`Error importing message for thread ${threadId}:`, msgErr);
                }
              }
              
              importedCount++;
              setImportProgress(60 + Math.floor((importedCount / newTopics.length) * 30));
            } catch (err) {
              console.error("Error importing thread:", err);
              importErrors++;
            }
          }
          
          setImportProgress(100);
          
          const message = importErrors > 0 
            ? `Import partially successful! Added ${importedCount} conversations${promptsImported > 0 ? ` and ${promptsImported} new prompts` : ''} with ${importErrors} errors.` 
            : `Import successful! Added ${importedCount} conversations${promptsImported > 0 ? ` and ${promptsImported} new prompts` : ''}.${totalPromptsAfterImport > 0 ? ` Total prompts: ${totalPromptsAfterImport}` : ''}`;
          
          alert(message);
          
          alert(message);
          
        } else {
          // Replace all existing data
          setImportProgress(40);
          
          // Delete all existing threads
          await threadService.deleteAllThreads();
          setImportProgress(60);
          
          // Import all threads WITH MESSAGES
          let importedCount = 0;
          let importErrors = 0;
          
          for (const topic of importedTopics) {
            try {
              // Validate topic
              if (!topic.id || !topic.title) {
                console.error("Invalid topic:", topic);
                importErrors++;
                continue;
              }
              
              // Ensure messages array exists
              if (!Array.isArray(topic.messages)) {
                topic.messages = [];
              }
              
              // Create the thread first
              const threadId = await threadService.createThread(
                topic.title, 
                topic.model || 'claude-3-7-sonnet-20250219',
                topic.id, // Pass the original ID
                topic.createdAt,
                topic.lastUpdated
              );
              
              console.log(`Created thread ${threadId} with title: ${topic.title}`);
              
              // Now import each message
              for (const message of topic.messages) {
                try {
                  if (message.role === 'user') {
                    await threadService.addUserMessage(threadId, {
                      role: 'user',
                      content: message.content,
                      apiContent: message.apiContent || message.content,
                      files: message.files || []
                    }, message.id, message.timestamp);
                  } else if (message.role === 'assistant') {
                    await threadService.addAssistantMessage(
                      threadId,
                      message.content,
                      {
                        continuationInProgress: message.continuationInProgress || false,
                        continuationComplete: message.continuationComplete || true
                      },
                      message.id,
                      message.timestamp
                    );
                  }
                } catch (msgErr) {
                  console.error(`Error importing message for thread ${threadId}:`, msgErr);
                }
              }
              
              importedCount++;
              setImportProgress(60 + Math.floor((importedCount / importedTopics.length) * 30));
            } catch (err) {
              console.error("Error importing thread:", err);
              importErrors++;
            }
          }
          
          setImportProgress(100);
          
          const message = importErrors > 0 
            ? `Import partially successful! Replaced with ${importedCount} conversations${promptsImported > 0 ? ` and ${promptsImported} prompts` : ''} with ${importErrors} errors.` 
            : `Import successful! Replaced with ${importedCount} conversations${promptsImported > 0 ? ` and ${promptsImported} prompts` : ''}.`;
          
          alert(message);
        }
        
        // Reload the application
        window.location.reload();
        
      } catch (error) {
        console.error("Import error:", error);
        alert("Failed to import data. Error: " + error.message);
      } finally {
        setIsImporting(false);
        setImportProgress(0);
        closeImportDialog();
      }
    };
    
    reader.onerror = () => {
      alert("Failed to read the file");
      closeImportDialog();
      setIsImporting(false);
      setImportProgress(0);
    };
    
    reader.readAsText(importFile);
  };

  // Handle reset application - updated to delete IndexedDB threads
  const handleReset = async () => {
    // Show confirmation dialog
    const isConfirmed = window.confirm(
      "WARNING: This will permanently delete all your conversation threads, settings, and API keys. This action cannot be undone. Are you sure you want to reset Blue Comet to its default state?"
    );
    
    if (isConfirmed) {
      try {
        // Delete all threads from IndexedDB
        await threadService.deleteAllThreads();
        
        // Clear all relevant items from localStorage
        localStorage.removeItem("anthropic_api_key");
        localStorage.removeItem("anthropic_api_key_encrypted");
        localStorage.removeItem("anthropic_model");
        localStorage.removeItem("conversation_context_level");
        localStorage.removeItem("show_resource_warnings");
        localStorage.removeItem("prompt_library");
        localStorage.removeItem("promptLibrary");
        localStorage.removeItem("user_prompts");
        
        // Completely remove rate limit tracking data
        localStorage.removeItem("anthropic_rate_limit_state");
        
        // CRITICAL FIX: Reset free trial state as well
        if (trialManager && typeof trialManager.resetTrialStateFlags === 'function') {
          trialManager.resetTrialStateFlags();
        }
        
        // Also clear any other related localStorage items that might be present
        const keysToCheck = ["anthropic_", "claude_", "api_", "rate_limit_", "freeTrial"];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && keysToCheck.some(prefix => key.includes(prefix))) {
            localStorage.removeItem(key);
          }
        }
        
        console.log("All data has been reset");
        
        // Reset state
        setApiKey('');
        setMaskedKey('');
        setSelectedModel('claude-3-7-sonnet-20250219');
        setContextLevel('maximum');
        setHasStoredKey(false);
        
        // Show confirmation message
        alert("Blue Comet has been reset to default settings. The application will reload now.");
        
        // Reload the application
        window.location.reload();
      } catch (error) {
        console.error("Error during reset:", error);
        alert("Error during reset: " + error.message);
      }
    }
  };

  if (!isOpen) return null;

  // First-time user welcome banner
  const isFirstTimeUser = !hasStoredKey;

  return (
    <div className="settings-overlay">
      <div className="settings-container">
        <div className="settings-header">
          <h2>Settings</h2>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>
        
        <div className="settings-content">
          {/* Welcome Banner - First time setup */}
          {isFirstTimeUser && (
            <div className="settings-welcome-banner">
              <h3>Welcome to Blue Comet! 🚀</h3>
              <p>
                To start using Blue Comet, you'll need an Anthropic API key. This key lets you connect to Claude AI models.
              </p>
              <a 
                href="https://console.anthropic.com/settings/keys" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                → How to get an Anthropic API key
              </a>
              <p className="security-note">
                Your API key is stored securely in your browser and never sent to any server other than Anthropic's API.
              </p>
            </div>
          )}
          
          {/* API Configuration Section */}
          <div className="settings-section">
            <h3>API Configuration</h3>
            
            <div className="form-group">
              <label htmlFor="api-key-input">Anthropic API Key</label>
              <div className="api-key-input">
                <input
                  id="api-key-input"
                  type={showApiKey ? "text" : "password"}
                  value={showApiKey ? apiKey : (hasStoredKey ? maskedKey : apiKey)}
                  onChange={handleApiKeyChange}
                  placeholder="Enter your Anthropic API key"
                  className={isFirstTimeUser ? "highlight-input" : ""}
                />
                <button 
                  type="button"
                  className="toggle-visibility"
                  onClick={toggleApiKeyVisibility}
                >
                  {showApiKey ? "Hide" : "Show"}
                </button>
              </div>
              <p className="helper-text">
                Your API key is stored locally in your browser.
              </p>
            </div>
            
            <div className="form-group">
              <label htmlFor="model-select">Claude Model</label>
              <select
                id="model-select"
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
              >
                {CLAUDE_MODELS.map(model => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
              <p className="helper-text">
                Select which Claude model to use for your conversations.
              </p>
            </div>
            
            <div className="form-group">
              <label htmlFor="context-level-select">Conversation Context</label>
              <select
                id="context-level-select"
                value={contextLevel}
                onChange={(e) => setContextLevel(e.target.value)}
              >
                {CONTEXT_LEVELS.map(level => (
                  <option key={level.id} value={level.id}>
                    {level.name}
                  </option>
                ))}
              </select>
              <p className="helper-text">
                Control how much conversation history is included in API calls. Lower settings use fewer tokens and may be faster.
              </p>
            </div>
            
            {/* Resource Warnings Toggle */}
            <div className="feature-toggle-group">
              <div className="feature-toggle-header">
                <div className="feature-icon">📊</div>
                <div className="feature-title">API Resource Warnings</div>
                <div className="toggle-switch-container">
                  <input
                    id="resource-warnings-toggle"
                    type="checkbox"
                    checked={showResourceWarnings}
                    onChange={(e) => setShowResourceWarnings(e.target.checked)}
                    className="toggle-switch-input"
                  />
                  <label htmlFor="resource-warnings-toggle" className="toggle-switch">
                    <span className="toggle-switch-slider"></span>
                  </label>
                  <span className="toggle-status">
                    {showResourceWarnings ? 'On' : 'Off'}
                  </span>
                </div>
              </div>
              <p className="feature-description">
                Get notified when approaching API rate limits to prevent service interruptions.
                Warnings appear when you reach approximately 20% of remaining resources.
              </p>
            </div>
          </div>
          
          {/* Storage Stats - Render any children passed to Settings */}
          {children}
          
          {/* Data Management Section */}
          <div className="settings-section">
            <h3>Data Management</h3>
            
            <div className="data-management-buttons">
              <button 
                className="action-button export-button"
                onClick={handleExportData}
                disabled={isExporting || isImporting}
              >
                <span>💾</span> Export Conversations
                {isExporting && (
                  <span className="progress-indicator">({exportProgress}%)</span>
                )}
              </button>
              <button 
                className="action-button import-button"
                onClick={() => document.getElementById('import-file').click()}
                disabled={isExporting || isImporting}
              >
                <span>📂</span> Import Conversations
                {isImporting && (
                  <span className="progress-indicator">({importProgress}%)</span>
                )}
              </button>
              <input
                type="file"
                id="import-file"
                accept=".json"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={showImportDialog}
              />
            </div>
            <p className="helper-text">
              Export your conversations to back them up or transfer them to another device.
              Import previously exported conversations to restore them.
            </p>
          </div>

          {/* Reset Application Section */}
          <div className="settings-section danger-section">
            <h3>Reset Application</h3>
            
            <div className="warning-box">
              <p className="warning-title">Warning: Permanent Data Deletion</p>
              <p className="warning-text">
                Resetting Blue Comet will permanently delete all your saved conversations, settings, and API keys. 
                This action is equivalent to a fresh installation and cannot be undone.
              </p>
            </div>
            
            <div className="reset-button-container">
              <button 
                className="reset-button"
                onClick={handleReset}
                disabled={isExporting || isImporting}
              >
                Reset Blue Comet
              </button>
            </div>
          </div>
        </div>
        
        <div className="settings-footer">
          <button 
            className="cancel-button" 
            onClick={handleClose}
          >
            Cancel
          </button>
          <button 
            className="save-button" 
            onClick={handleSave}
            disabled={isExporting || isImporting}
          >
            Save Changes
          </button>
        </div>
      </div>
      
      {/* Import Options Dialog */}
      {showImportOptions && (
        <div className="settings-overlay">
          <div className="settings-container" style={{ maxWidth: '500px' }}>
            <div className="settings-header">
              <h3>Import Options</h3>
              <button className="close-button" onClick={closeImportDialog}>×</button>
            </div>
            <div className="settings-content">
              <p style={{ fontSize: '16px', marginBottom: '16px' }}>
                Choose how to import your conversations:
              </p>
              
              <div style={{ marginBottom: '20px' }}>
                <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <h4 style={{ fontSize: '16px', margin: '0 0 8px 0', fontWeight: '600' }}>Combine:</h4>
                  <p style={{ fontSize: '14px', margin: '0', lineHeight: '1.4' }}>
                    Add new conversations from the import file to your existing ones. 
                    Duplicate conversations will be skipped. Your current conversations will remain intact.
                  </p>
                </div>
                
                <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <h4 style={{ fontSize: '16px', margin: '0 0 8px 0', fontWeight: '600' }}>Replace:</h4>
                  <p style={{ fontSize: '14px', margin: '0', lineHeight: '1.4' }}>
                    Remove all your current conversations and replace them with those from the import file.
                    This will permanently delete your existing conversations.
                  </p>
                </div>
              </div>
              
              <div style={{ padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '4px', marginTop: '16px', fontSize: '14px' }}>
                <span style={{ fontWeight: '500' }}>Selected file:</span> <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{importFile?.name}</span>
              </div>
            </div>
            
            <div className="settings-footer">
              <button 
                className="cancel-button" 
                onClick={closeImportDialog}
              >
                Cancel
              </button>
              <button 
                className="save-button" 
                onClick={() => processImport(true)}
                disabled={isImporting}
                style={{ backgroundColor: '#1677ff' }}
              >
                Combine
              </button>
              <button 
                className="reset-button" 
                onClick={() => processImport(false)}
                disabled={isImporting}
              >
                Replace
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;